import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { db } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";

export const SectionHero = () => {
  const [title, setTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState({
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const MAX_RETRIES = 3;

  // Fungsi untuk scroll ke section berikutnya
  const scrollToNextSection = () => {
    const nextSection = document.getElementById("date");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Mengambil data dari Firestore dengan retry logic
  useEffect(() => {
    const fetchEventData = async (attempt = 0) => {
      try {
        if (!db) {
          throw new Error("Firebase not initialized");
        }

        const eventRef = doc(db, "semuaKegiatan", "JOTR2026");
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          setTitle(eventData.title ? eventData.title.toUpperCase() : null);
          setError(null);
        } else {
          throw new Error("Jakarta Open Trail Run document not found");
        }
      } catch (err) {
        console.error("Firebase error:", err);

        if (attempt < MAX_RETRIES) {
          setRetryCount(attempt + 1);
          setTimeout(
            () => fetchEventData(attempt + 1),
            Math.pow(2, attempt) * 1000
          );
        } else {
          setError(
            err.message || "Failed to load data. Please try again later."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, []);

  // Countdown logic
  useEffect(() => {
    const targetDateStr = "2026-07-24T00:00:00+07:00";
    const target = new Date(targetDateStr);

    const updateCountdown = () => {
      const now = new Date();
      
      if (now >= target) {
        setCountdown({
          months: 0,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        return;
      }

      // Calculate Months
      let months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
      let tempTarget = new Date(now);
      tempTarget.setMonth(now.getMonth() + months);

      // If adding the months puts us past the target (e.g., target is at midnight but now is during the day),
      // then we have one less full month of countdown.
      if (tempTarget > target) {
        months--;
        tempTarget = new Date(now);
        tempTarget.setMonth(now.getMonth() + months);
      }
      
      const timeLeft = target.getTime() - tempTarget.getTime();

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      // Note: State property is still named 'days' but conceptually it is 'months' for the first block
      // The UI maps: Block 1 -> countdown.days (Label: Months), Block 2 -> countdown.hours (Label: Days)... 
      // Wait, let's check the UI mapping again.
      // UI: 
      // 1. {countdown.days} Label: Months
      // 2. {countdown.hours} Label: Days
      // 3. {countdown.minutes} Label: Hours
      // 4. {countdown.seconds} Label: Minutes
      // This mapping is confusing. I should rename the state keys or values to match the UI labels.
      
      setCountdown({
        months,
        days,
        hours,
        minutes,
        seconds,
      });
      // The design has 4 blocks. 
      // Image: 6 Months, 8 Days, 9 Hours, 4 Minutes? (Seconds is 28). There are 5 items in text?? 
      // Image shows 5 numbers? No, let's look at image 1.
      // Image 1: 6 Months, 8 Days, 9 Hours, 4 Minutes, 28 Seconds.
      // It has 5 columns?
      // "6 Months", "8 Days", "9 Hours", "4 Minutes", "28 Seconds"
      // My code has 4 blocks.
      // UI Code:
      // Block 1: {countdown.days} Label: Months
      // Block 2: {countdown.hours} Label: Days
      // Block 3: {countdown.minutes} Label: Hours
      // Block 4: {countdown.seconds} Label: Minutes
      // MISSING SECONDS!
      
      // To match the image (which has 5 data points: Months, Days, Hours, Minutes, Seconds),
      // we need 5 blocks in the UI. Currently there are 4 in the code.
      // Current Code has 4 blocks.
      // I will map: 
      // Block 1: Months
      // Block 2: Days
      // Block 3: Hours
      // Block 4: Minutes
      // And drop seconds? Or combine?
      // The user said "fix countdown". The image shows 5 items.
      // I only have 4 slots in the grid (grid-cols-4).
      // I should probably stick to Days/Hours/Minutes/Seconds or Months/Days/Hours/Minutes.
      // Given it's long term, Months/Days/Hours/Minutes is better.
      // So I will map:
      // days -> months
      // hours -> days
      // minutes -> hours
      // seconds -> minutes
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Reset scroll saat unmount
  useEffect(() => {
    return () => {
      window.scrollTo(0, 0);
    };
  }, []);

  const socialMedia = [
    { icon: faFacebook, link: "#" },
    { icon: faInstagram, link: "https://www.instagram.com/jotr_gepang" },
    { icon: faTwitter, link: "#" },
  ];

  return (
    <section
      id="hero"
      className="w-full -mt-20 pt-20 min-h-screen text-black text-center flex flex-col justify-center relative"
      style={{
        backgroundImage: "url('/bg/bghero11.jpg')",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 z-0"></div>

      {/* Desktop: Social media icons (hidden on mobile) */}
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-0 hidden md:flex flex-col items-center gap-4">
        {socialMedia.map((social, index) => (
          <motion.a
            key={index}
            href={social.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white text-xl sm:text-2xl bg-opacity-50 w-8 h-8 flex items-center justify-center rounded-full hover:text-lime-200 transition-colors"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.7 + index * 0.1,
              ease: [0.4, 0, 0.2, 1],
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon icon={social.icon} />
          </motion.a>
        ))}
      </div>
      {/* End Desktop: Social media icons */}

      {/* Konten hanya ditampilkan jika tidak loading */}
      {!loading && (
        <div className="relative text-white text-center px-6 z-1 flex flex-col justify-center flex-grow mt-2 md:mt-24 md:pb-24 gap-2">
          {error || !title ? (
            // State error
            <p className="text-white">Failed to load event data.</p>
          ) : (
            // State sukses
            <>
              <motion.img
                src="jotrWhite.svg"
                alt="JOTR Logo"
                className="h-36 md:h-52 mx-auto object-contain"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <motion.p
                className="text-2xl md:text-5xl lg:text-6xl pt-4 font-bold mx-auto text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                JOTR CIANJUR SERIES 2026
              </motion.p>
              <motion.p
                className="text-sm md:text-4xl lg:text-3xl pb-10 font-normal mx-auto text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                SOUTHEAST ASIAN TRAIL RUNNING CUP
              </motion.p>
              {/* Mobile and Desktop: Countdown timer */}
              {/* Mobile and Desktop: Countdown timer */}
              <div className="grid grid-cols-5 gap-2 mb-8 text-center max-w-lg mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.months}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Months</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.days}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Days</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.hours}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Hours</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.minutes}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Minutes</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.seconds}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Seconds</p>
                </motion.div>
              </div>
              {/* End Mobile and Desktop: Countdown timer */}
              <motion.h2
                className="text-sm sm:text-lg md:text-lg font-normal uppercase text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 1.0,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                #JOTRSEATRC2026
              </motion.h2>
            </>
          )}
        </div>
      )}

      {/* Mobile and Desktop: Scroll button */}
      {!loading && !error && title && (
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-0">
          <motion.div
            className="cursor-pointer text-white animate-bounce z-0"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            onClick={scrollToNextSection}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-4xl" />
          </motion.div>
        </div>
      )}
      {/* End Mobile and Desktop: Scroll button */}
    </section>
  );
};
